export default function MLMElephant() {
  return (
    <section className="relative py-20 bg-[#0f1d2e]">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
            ADDRESSING THE <span className="text-[#DC2626]">ELEPHANT</span>
          </h2>
          <p className="text-xl text-gray-300">
            Let's talk about the MLM thing.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-6">
          {/* FAQ 1 - MLM Question */}
          <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2332] rounded-xl p-8 border border-[#DC2626]/20">
            <h3 className="text-white font-black text-2xl mb-4">
              "Is this one of those MLM things?"
            </h3>
            <div className="text-gray-300 text-lg leading-relaxed space-y-4">
              <p>
                <strong className="text-white">Yes, technically it's an MLM.</strong> I don't care. I'm not asking you to sell it. I'm just showing you the tool I use because the product is insanely underpriced for what it does.
              </p>
              <p>
                Here's the deal: LegalShield uses a multi-level marketing structure to distribute their legal plans. That means there are "associates" who sell memberships and earn commissions. Some of them are pushy. Some of them are great. I'm neither – I'm just a real estate investor who got burned by a contractor and found a tool that saved me $4,500.
              </p>
              <p>
                <strong className="text-white">I don't care about the business opportunity.</strong> I care about having a lawyer on speed dial for $30/month. If you want to ignore the MLM side entirely and just buy the coverage, that's exactly what I'm helping you do here.
              </p>
              <p>
                When you click through my links, you might see mentions of "joining the opportunity" or "becoming an associate." <strong className="text-white">Ignore all of that.</strong> You're just buying legal coverage. That's it.
              </p>
            </div>
          </div>

          {/* FAQ 2 - Business Coverage */}
          <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2332] rounded-xl p-8 border border-[#DC2626]/20">
            <h3 className="text-white font-black text-2xl mb-4">
              "Will this cover my rentals / LLC / business?"
            </h3>
            <div className="text-gray-300 text-lg leading-relaxed space-y-4">
              <p>
                <strong className="text-white">Not automatically.</strong> The personal plan covers <em>you as an individual</em> – traffic tickets, contract disputes in your name, identity theft, wills, etc.
              </p>
              <p>
                <strong className="text-white">It does NOT cover:</strong>
              </p>
              <ul className="list-none space-y-2 ml-6">
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span>Your LLC or business entity</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span>Rental properties owned by your LLC</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span>Tenant evictions or lease disputes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span>Business contracts or vendor disputes</span>
                </li>
              </ul>
              <p>
                <strong className="text-white">The fix:</strong> If you're a landlord or business owner, you need to add the <strong>Home Business Supplement</strong> (for small operations) or upgrade to the <strong>Small Business Plan</strong> (for serious businesses).
              </p>
              <p>
                <strong className="text-[#DC2626]">Don't be cheap. Get the right armor.</strong> I made this mistake once – bought the personal plan thinking it would cover my rental dispute. It didn't. I had to upgrade mid-crisis. Learn from my mistake.
              </p>
              <p className="pt-4">
                <a 
                  href="#business-path" 
                  className="text-[#DC2626] hover:text-[#B91C1C] font-bold underline cursor-pointer"
                >
                  → See Path 2 above for landlord/business options
                </a>
              </p>
            </div>
          </div>

          {/* FAQ 3 - Warning for Investors */}
          <div className="bg-gradient-to-br from-[#0a1628] to-[#1a2332] rounded-xl p-8 border border-[#DC2626]/20">
            <h3 className="text-white font-black text-2xl mb-4">
              "Warning for my investors / business matters"
            </h3>
            <div className="text-gray-300 text-lg leading-relaxed space-y-4">
              <p>
                <strong className="text-white">If you're a real estate investor, landlord, or business owner:</strong> The personal plan is NOT enough. You need business coverage.
              </p>
              <p>
                I learned this the hard way. I bought the personal plan thinking it would cover my contractor dispute on a rental property. It didn't. The personal plan only covers <em>me as an individual</em>, not my LLC or business entities.
              </p>
              <p>
                <strong className="text-white">Here's what you need to know:</strong>
              </p>
              <ul className="list-none space-y-2 ml-6">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span><strong className="text-white">Personal Plan:</strong> Covers you personally – tickets, personal contracts, identity theft, wills.</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span><strong className="text-white">Home Business Supplement:</strong> Add-on for small landlords or side hustles (a few rentals, small business).</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-[#DC2626] text-xl flex-shrink-0 mt-0.5"></i>
                  <span><strong className="text-white">Small Business Plan:</strong> Full coverage for serious businesses, multiple properties, partnerships, employees.</span>
                </li>
              </ul>
              <p>
                <strong className="text-[#DC2626]">Don't make my mistake.</strong> If you own rentals or run a business, get the right coverage from day one.
              </p>
              <p className="pt-4">
                <a 
                  href="#business-path" 
                  className="text-[#DC2626] hover:text-[#B91C1C] font-bold underline cursor-pointer"
                >
                  → See Path 2 above for landlord/business options
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Link to Full Story */}
        <div className="text-center mt-12">
          <p className="text-gray-300 text-xl mb-4">
            Want the full story of how I discovered this?
          </p>
          <a
            href="/about"
            className="inline-flex items-center gap-2 text-[#DC2626] hover:text-[#B91C1C] font-black text-xl underline cursor-pointer transition-colors"
          >
            Read: My $4,500 Mistake
            <i className="ri-arrow-right-line text-2xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
